// @flow
import React, {
    useEffect, useState, useContext, useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from 'grommet';
import type { LeafletContext } from 'react-leaflet/src';
import { withLeaflet } from 'react-leaflet';
import { withConfirmation } from 'speed-js-react';
import { MapContext } from 'esm-web-pro/containers/map/context';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { ApiClient } from 'speed-js-core';
import PolygonAttributes from 'esm-web-pro/containers/map/polygon/PolygonAttributes';
import { IntlShape, defineMessages, injectIntl } from 'react-intl';
import LeavePageDialog from 'esm-web-pro/components/dialogs/LeavePageDialog';
import { EditorContext } from '../editor/BaseEditor';
import MapObjectForm from '../forms/MapObjectForm';
import ActionButtons from '../../../components/forms/ActionButtons';

const messages = defineMessages({
    cancel: {
        id: 'network-draw-block-message',
        defaultMessage: 'The drawing will stop and already drawn items will not be saved',
    },
});

type Props = {
    intl: IntlShape,
    postPolygonObject: (resourceName: string, id: uuid, data: Object<any>) => Promise<any>,
    leaflet: LeafletContext,
    getAttributes: Function,
    attributes: Object,
}

const PolygonRoutes = (props: Props) => {
    const {
        leaflet: { map }, postPolygonObject, getAttributes, attributes, intl: { formatMessage },
    } = props;

    const [name, setName] = useState('');
    const [polygon, setPolygon] = useState(null);
    const [layersToRemove, setLayersToRemove] = useState([]);

    const [canDraw, setCanDraw] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const [isDisabled, setIsDisabled] = useState(true);
    const [isDisabledSave, setIsDisabledSave] = useState(true);

    const history = useHistory();
    const { uuid } = useContext(MapContext);
    const { _loadFeatures } = useContext(EditorContext);

    // Remove objects drawn on the map
    const removeLayers = (layers) => {
        layers.forEach((layer) => layer.remove());
    };

    // Change the status of the save Button
    useEffect(() => {
        setIsDisabledSave(!name);
        if (name && polygon) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [polygon, name]);

    // Enable drawing mode and and listen for creation of polygon object
    useEffect(() => {
        if (!polygon && canDraw) {
            map.pm.enableDraw('Polygon', { snappable: true });
            map.on('pm:create', (e) => {
                const { layer } = e;
                setLayersToRemove([...layersToRemove, layer]);
                setPolygon((layer.toGeoJSON().geometry));
                map.pm.disableDraw('Polygon');
            });
        }
        return () => {
            removeLayers(layersToRemove);
            map.off('pm:create');
            map.pm.disableDraw('Polygon');
        };
    }, [map, polygon, canDraw]);

    const onChange = (e) => {
        const { value } = e.target;
        setName(value);
        // getAttributes({ name: value });
    };

    const goBack = () => {
        history.push(history.location.pathname.split('/polygon')[0]);
    };

    const onClose = () => {
        setName('');
        setPolygon(null);
        removeLayers(layersToRemove);
        goBack();
    };

    const onSave = () => {
        getAttributes({ name });
        setCanDraw(true);
    };

    // Create data object and make POST request to the backend
    const onSubmit = (e) => {
        e.preventDefault();
        if (name !== '' && polygon !== null) {
            const data = {
                name,
                polygon,
            };
            postPolygonObject('gisRegions.geo', uuid, data)
                .then(() => {
                    removeLayers(layersToRemove);
                    _loadFeatures();
                    goBack();
                })
                .catch((err) => console.log(err));
        }
    };

    return (
        <Box height="100%">
            {openDialog
            && (
                <LeavePageDialog
                    onProceed={onClose}
                    onClose={() => setOpenDialog(false)}
                    message={formatMessage(messages.cancel)}
                />
            )
            }
            {
                getAttributes ? (
                    <>
                        <MapObjectForm
                            key="network"
                            resourceName="gisRegions.geo"
                            excludeFields={['polygon']}
                            resourceArguments={[uuid]}
                            onChange={onChange}
                        />
                        <ActionButtons
                            onClose={() => setOpenDialog(true)}
                            onSave={onSave}
                            disabled={isDisabledSave}
                        />
                    </>
                ) : (
                    <>
                        <PolygonAttributes polygon={attributes} />
                        <ActionButtons
                            onClose={() => setOpenDialog(true)}
                            onSave={onSubmit}
                            disabled={isDisabled}
                        />
                    </>
                )
            }
        </Box>
    );
};

const apiCallback = ((apiClient: ApiClient) => ({
    postPolygonObject: {
        send: (resourceName, map, data) => apiClient.resources[resourceName].create(map, data),
        success: ((res) => res),
        error: ((err) => err),
    },
}));

export default connectAPI(apiCallback)(withConfirmation(injectIntl(withLeaflet(PolygonRoutes))));
